import DateSearch from "../DateSearch";
import FlyingFromLocation from "./FlyingFromLocation";
import FlyingToLocation from "./FlyingToLocation";
import {useNavigate} from "react-router-dom";
import TripDurationSearch from "@/components/hero/hero-10/TripDurationSearch.jsx";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {DateObject} from "react-multi-date-picker";
import reportEvent from "@/utils/reportEvent.js";
import RangeSlider from "@/components/common/RangeSlider.jsx";

// Full-range defaults for each "advanced filter" slider - a slider left untouched at its default
// must mean "no filter", not "filter set to the full range" (those are the same value, but only
// the first should skip appending the query param at all - see the search-button handler).
const BUDGET_RANGE = {min: 0, max: 5000};
const TEMPERATURE_RANGE = {min: -10, max: 40};
const DEPARTURE_TIME_RANGE = {min: 0, max: 24};

function toAPIDate(date) {

    function appendZero(month) {
        return month < 10 ? "0" + month : month;
    }

    return appendZero(date.year) + "-" + appendZero(date.month) + "-" + appendZero(date.day);
}

function toDate(tripDate) {
    return new Date(tripDate)
}

// The two real destinations a search can land on: a combined flight+hotel trip search
// ("/tour-list-v1") or a flights-only search ("/flights" - see src/pages/flights/index.jsx).
// Kept as a small constant pair rather than a generic prop because that's genuinely the whole
// space today - see FlightsHotelToggle below for the UI that switches between them.
const COMBO_RESULTS_PATH = "/tour-list-v1";
const FLIGHTS_ONLY_RESULTS_PATH = "/flights";

// A real segmented switch: one pill-shaped track (light grey, like an iOS/macOS segmented
// control) with a single sliding dark "thumb" behind whichever option is active, rather than two
// independent buttons sitting side by side - two separately-styled buttons (even once both were
// individually legible) still read as "two random buttons", not one cohesive switch.
//
// The thumb's left/width are measured off the actual buttons via refs (getBoundingClientRect),
// not assumed to be a 50/50 split - "Loty + hotel" + the "Polecane" badge is genuinely wider than
// "Tylko loty", so a CSS-only calc(50% - 4px) thumb either left a visible gap past the shorter
// segment or didn't fully cover the longer one, depending on which state was active. Forcing both
// segments to equal width (flex: 1 1 0) was the earlier attempt at a fix and had the same
// underlying problem: content wider than its forced share doesn't shrink below min-content, so
// the segments quietly stopped being equal-width again anyway. Measuring real DOM widths sidesteps
// the whole class of bug instead of re-guessing another CSS trick.
//
// A follow-up bug in *that* fix: it only re-measured when `flightsOnly` changed, so the thumb
// was still correct only at the instant the effect first ran. It went stale on anything that
// reflows the buttons without touching that state - a window resize, a responsive breakpoint
// change, or (notably) browser zoom, which can shift measured text width by a pixel or two from
// subpixel glyph-metric rounding at non-100% zoom levels. A ResizeObserver on the buttons
// themselves (not the `flightsOnly` dependency) is what keeps the thumb glued to their *actual*
// current size instead of a snapshot from the last toggle click. Re-measuring once webfonts
// finish loading covers the same class of bug for the FOUT->real-font swap on first paint, which
// can likewise change text width without firing a resize.
const FlightsHotelToggle = ({ flightsOnly, onChange }) => {
    const comboRef = useRef(null);
    const flightsOnlyRef = useRef(null);
    const [thumbRect, setThumbRect] = useState(null);

    useLayoutEffect(() => {
        const measure = () => {
            const target = (flightsOnly ? flightsOnlyRef.current : comboRef.current);
            if (target) {
                setThumbRect({ left: target.offsetLeft, width: target.offsetWidth });
            }
        };

        measure();

        let observer;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(measure);
            if (comboRef.current) observer.observe(comboRef.current);
            if (flightsOnlyRef.current) observer.observe(flightsOnlyRef.current);
        }

        // Fallback/extra coverage: some browsers don't reflow the ResizeObserver-visible box on
        // page zoom even though rendered glyph widths shift, and webfont swap-in doesn't
        // necessarily change the button's own box size either.
        window.addEventListener("resize", measure);
        let cancelled = false;
        document.fonts?.ready?.then(() => {
            if (!cancelled) measure();
        }).catch(() => {});

        return () => {
            cancelled = true;
            observer?.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [flightsOnly]);

    // Sized by padding, not a fixed height class - this is a secondary mode-switch that sits
    // above the actual search fields, not the box's primary control, so it should read as a
    // compact chip/tab, not a full-height button rivaling the "Szukaj" button below it (an
    // earlier "give it more room" pass overcorrected into exactly that).
    return (
        <div
            className="relative d-inline-flex items-center bg-light-2 border-light rounded-100 mb-15"
            role="group"
            aria-label="Rodzaj wyszukiwania"
            style={{ padding: 3 }}
        >
            {thumbRect && (
                <div
                    className="absolute bg-dark-1 rounded-100 shadow-1"
                    aria-hidden="true"
                    style={{
                        top: 3,
                        bottom: 3,
                        left: thumbRect.left,
                        width: thumbRect.width,
                        transition: "left .25s cubic-bezier(0.4, 0, 0.2, 1), width .25s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                />
            )}
            <button
                ref={comboRef}
                type="button"
                className={"relative d-flex items-center justify-center py-10 px-20 text-13 fw-500 " + (!flightsOnly ? "text-white" : "text-dark-1")}
                onClick={() => onChange(false)}
                style={{ border: "none", background: "transparent", zIndex: 1, whiteSpace: "nowrap", transition: "color .25s ease" }}
            >
                Loty + hotel
                <span
                    className={"text-10 fw-600 ls-1 text-uppercase ml-10 px-10 rounded-100 d-inline-flex items-center " + (!flightsOnly ? "bg-white text-dark-1" : "bg-blue-1-05 text-blue-1")}
                    style={{ whiteSpace: "nowrap", flexShrink: 0, lineHeight: "1", paddingTop: 6, paddingBottom: 6, transition: "background-color .25s ease, color .25s ease" }}
                >
                    Polecane
                </span>
            </button>
            <button
                ref={flightsOnlyRef}
                type="button"
                className={"relative d-flex items-center justify-center py-10 px-20 text-13 fw-500 " + (flightsOnly ? "text-white" : "text-dark-1")}
                onClick={() => onChange(true)}
                style={{ border: "none", background: "transparent", zIndex: 1, whiteSpace: "nowrap", transition: "color .25s ease" }}
            >
                Tylko loty
            </button>
        </div>
    );
};

const MainFilterSearchBox = ({
                                 customStyle, initFromLocation = null, initToLocation = "", initFromLocationCode = null,
                                 initTripDate = [
                                     new DateObject(),
                                     new DateObject().add(6, "months"),
                                 ], initTripDuration = {
        min: 1,
        max: 14
    }, initWeekendOnly = false, refreshOnReload = false,
                                 initSearchTypeSelect = "", initSameAirport = false, searchResultsPath = "/tour-list-v1",
                                 // Map-page variant support: the map itself is the "where can I go" answer, so it
                                 // has no use for a destination field or the flights/hotel mode toggle - and it
                                 // re-fetches its own marker set in place rather than navigating to a results page.
                                 // All three default to the box's original behaviour, so every other caller
                                 // (tour-list-v1, /flights, the hero variants) is unaffected.
                                 hideDestination = false, hideModeToggle = false, onSearch = null,
                                 gridStyle = "-col-4"
                             }) => {
    const navigate = useNavigate();
    const [fromLocation, setFromLocation] = useState(initFromLocation);
    const [fromLocationCode, setFromLocationCode] = useState(initFromLocationCode);
    const [toLocation, setToLocation] = useState(initToLocation);
    const [tripDate, setTripDate] = useState(initTripDate);
    const [tripDuration, setTripDuration] = useState(initTripDuration);
    const [searchType, setSearchType] = useState(initSearchTypeSelect);
    const [sameAirport, setSameAirport] = useState(initSameAirport);
    const [weekendOnly, setWeekendOnly] = useState(initWeekendOnly);
    // Defaults to whichever mode the page this box is already rendered on represents (e.g. the
    // /flights page passes searchResultsPath="/flights", so its own box opens on "Tylko loty"),
    // but the toggle can always override that for the next search.
    const [flightsOnly, setFlightsOnly] = useState(searchResultsPath === FLIGHTS_ONLY_RESULTS_PATH);

    // "Advanced filters" - collapsed by default (see the toggle button below the main fields) so
    // adding these doesn't turn the primary, everyday search flow into a wall of controls. Only a
    // small fraction of searches will ever need a budget/temperature/departure-time constraint;
    // the fields everyone uses (from/to/dates/duration) stay exactly as prominent as before.
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [anyAirport, setAnyAirport] = useState(false);
    const [budgetRange, setBudgetRange] = useState([BUDGET_RANGE.min, BUDGET_RANGE.max]);
    const [temperatureRange, setTemperatureRange] = useState([TEMPERATURE_RANGE.min, TEMPERATURE_RANGE.max]);
    const [departureTimeRange, setDepartureTimeRange] = useState([DEPARTURE_TIME_RANGE.min, DEPARTURE_TIME_RANGE.max]);

    useEffect(() => {
        if (initSameAirport && document.getElementById("same-airport") !== null) {
            document.getElementById("same-airport").checked = true;
        }
    }, []);

    const onWeekendCheckbox = (e) => {
        const checked = e.target.checked;
        setWeekendOnly(checked);
        if (checked) {
            console.log("Setting new duration")
            let newMin = tripDuration.min >= 5 ? 1 : tripDuration.min;
            setTripDuration({
                "min": newMin,
                "max": 5
            })
        }
    }

    const onSameAirportCheckbox = (e) => {
        const checked = e.target.checked;
        setSameAirport(checked);
    }


    return (
        <>
            <div
                className={"mainSearch " + gridStyle + " bg-white shadow-1 rounded-4 pr-20 py-20 lg:px-20 lg:pt-5 lg:pb-20 mt-15 " + customStyle}>
                {!hideModeToggle && (
                    <div className="pl-20 lg:pl-0">
                        <FlightsHotelToggle flightsOnly={flightsOnly} onChange={setFlightsOnly}/>
                    </div>
                )}
                <div className="button-grid items-center">
                    <div style={anyAirport ? {opacity: 0.45, pointerEvents: "none"} : undefined}
                         title={anyAirport ? "Wyłączone - zaznaczono \"Dowolne lotnisko w Polsce\" poniżej" : undefined}>
                        <FlyingFromLocation onLocationChange={setFromLocation} initSearchValue={initFromLocation}/>
                    </div>
                    {/* End Location Flying From */}

                    {!hideDestination && (
                        <FlyingToLocation onLocationChange={setToLocation} initSearchValue={initToLocation}/>
                    )}
                    {/* End Location Flying To */}

                    <div className="searchMenu-date px-30 lg:py-20 lg:px-0 js-form-dd js-calendar">
                        <div>
                            <h4 className="text-15 fw-500 ls-2 lh-16">Daty wyjazdu</h4>
                            <DateSearch onDateChange={setTripDate} initDates={initTripDate}/>
                        </div>
                    </div>
                    {/* End Depart */}

                    {/*<div className="searchMenu-date px-30 lg:py-20 lg:px-0 js-form-dd js-calendar">*/}
                    {/*  <div>*/}
                    {/*    <h4 className="text-15 fw-500 ls-2 lh-16">Najpóźniejszy powrót</h4>*/}
                    {/*    /!*<DateSearch onDateChange={setEndDate} />*!/*/}
                    {/*  </div>*/}
                    {/*</div>*/}
                    {/* End Return */}
                    <TripDurationSearch onDurationChange={setTripDuration} initialState={tripDuration}
                                        externalDurationChange={tripDuration}/>
                    {/*<GuestSearch />*/}
                    {/* End guest */}

                    <div className="button-item">
                        <button
                            className="mainSearch__submit button -blue-1 py-15 px-35 h-60 col-12 rounded-4 bg-dark-1 text-white"
                            onClick={() => {
                                reportEvent({
                                    category: "button-click",
                                    action: "search-button-clik"
                                })
                                const searchParams = new URLSearchParams();
                                const date = tripDate
                                // "Dowolne lotnisko w Polsce" (anyAirport) makes the implicit "no from = search
                                // every departure airport" behavior explicit/discoverable (see FlyingFromLocation)
                                // rather than something a user only finds by accident leaving the field blank.
                                if (!anyAirport && fromLocation !== null && (fromLocation.hasOwnProperty('code') || fromLocationCode !== null)) {
                                    searchParams.append("from", fromLocation.hasOwnProperty('code') ? fromLocation.code : fromLocationCode);
                                    searchParams.append("fromName", fromLocation.name ? fromLocation.name : fromLocation);
                                }
                                if (!hideDestination) {
                                    searchParams.append("destination", toLocation);
                                }
                                searchParams.append("dateFrom", toAPIDate(date[0]));
                                searchParams.append("dateTo", toAPIDate(date[1]));
                                searchParams.append("durationMin", tripDuration.min);
                                searchParams.append("durationMax", tripDuration.max);
                                searchParams.append("onlyWeekends", weekendOnly);
                                // The useful default is one best-value combination per city. The results page
                                // offers an explicit "show all combinations" escape hatch when users want depth.
                                searchParams.append("uniqueDestinations", true);
                                searchParams.append("searchType", searchType);
                                if(sameAirport) {
                                    searchParams.append("requireSameReturnAirport", sameAirport);
                                }
                                // Advanced filters - only sent when actually moved off their full-range default,
                                // so leaving them untouched reproduces the exact search this box always did.
                                if (budgetRange[1] < BUDGET_RANGE.max) {
                                    searchParams.append("maxPrice", budgetRange[1]);
                                }
                                if (temperatureRange[0] > TEMPERATURE_RANGE.min) {
                                    searchParams.append("minTemperature", temperatureRange[0]);
                                }
                                if (departureTimeRange[0] > DEPARTURE_TIME_RANGE.min) {
                                    searchParams.append("departureTimeMin", departureTimeRange[0]);
                                }
                                if (departureTimeRange[1] < DEPARTURE_TIME_RANGE.max) {
                                    searchParams.append("departureTimeMax", departureTimeRange[1]);
                                }

                                // Map-page variant: hand the built params back to the caller (which re-fetches
                                // its own marker set in place) instead of navigating to a results page.
                                if (onSearch) {
                                    onSearch(searchParams);
                                    return;
                                }

                                // The toggle picks which kind of search to run - flights-only or the
                                // combined flight+hotel trip search - independently of whatever page
                                // this box happens to be rendered on (searchResultsPath is just this
                                // instance's own initial default, e.g. the /flights page always opens
                                // pre-set to "Tylko loty").
                                const targetPath = flightsOnly ? FLIGHTS_ONLY_RESULTS_PATH : COMBO_RESULTS_PATH;
                                navigate(targetPath + "?" + searchParams.toString());
                                // refresh if already on the page
                                if (window.location.pathname === targetPath && refreshOnReload) {
                                    window.location.reload();
                                }
                            }}
                        >
                            <i className="icon-search text-20 mr-10"/>
                            Szukaj
                        </button>
                    </div>

                    {/* End search button_item */}

                </div>

                {/* Deliberately NOT .button-grid here (unlike the row above) - that class is a
                    real CSS Grid (grid-template-columns: auto auto 200px 200px min-content for
                    the default -col-4 size), which was squeezing each direct child below
                    (the checkbox row, the advanced-filters toggle, the advanced-filters panel)
                    into its own narrow grid column instead of letting them stack as normal
                    block content - the cause of the toggle button's text wrapping/centering
                    oddly into a cramped column. */}
                <div className="ml-20 searchSecondaryRow">
                    <div className="d-flex flex-column flex-lg-row justify-content-between">
                        <div className="col-auto mr-30">
                            <div className="form-checkbox d-flex items-center">
                                <input type="checkbox" id="weekends-only" checked={weekendOnly} onChange={onWeekendCheckbox} aria-label="Tylko weekendy"/>
                                <div className="form-checkbox__mark">
                                    <div className="form-checkbox__icon icon-check"/>
                                </div>
                                <div className="text-15 ml-10">Tylko weekendy</div>
                            </div>
                        </div>
                        <div className="col-auto mr-30">
                            <div className="form-checkbox d-flex items-center">
                                <input type="checkbox" id="same-airport" onChange={onSameAirportCheckbox} aria-label="Powrót na to samo lotnisko"/>
                                <div className="form-checkbox__mark">
                                    <div className="form-checkbox__icon icon-check"/>
                                </div>
                                <div className="text-15 ml-10">Powrót na to samo lotnisko</div>
                            </div>
                        </div>
                    </div>
                    {/* End Location Flying From */}

                    <button
                        type="button"
                        className="d-inline-flex items-center text-14 fw-500 text-blue-1 rounded-100 mt-15"
                        style={{
                            border: "1px solid var(--color-blue-1)", background: "transparent",
                            padding: "8px 16px", whiteSpace: "nowrap",
                        }}
                        onClick={() => setShowAdvanced((v) => !v)}
                        aria-expanded={showAdvanced}
                    >
                        <i className={"mr-5 " + (showAdvanced ? "icon-chevron-up" : "icon-chevron-down")}/>
                        Filtry zaawansowane
                    </button>
                    {showAdvanced && (
                        <div className="row y-gap-20 mt-15 pt-15 border-top-light">
                            <div className="col-md-4">
                                <RangeSlider
                                    label="Budżet na osobę"
                                    min={BUDGET_RANGE.min} max={BUDGET_RANGE.max} step={50}
                                    value={budgetRange} onChange={setBudgetRange}
                                    formatValue={(v) => v >= BUDGET_RANGE.max ? "bez limitu" : v + " zł"}
                                />
                            </div>
                            <div className="col-md-4">
                                <RangeSlider
                                    label="Temperatura w celu podróży"
                                    min={TEMPERATURE_RANGE.min} max={TEMPERATURE_RANGE.max} step={1}
                                    value={temperatureRange} onChange={setTemperatureRange}
                                    formatValue={(v) => v <= TEMPERATURE_RANGE.min ? "dowolna" : v + "°C"}
                                />
                            </div>
                            <div className="col-md-4">
                                <RangeSlider
                                    label="Godziny lotu tam"
                                    min={DEPARTURE_TIME_RANGE.min} max={DEPARTURE_TIME_RANGE.max} step={1}
                                    value={departureTimeRange} onChange={setDepartureTimeRange}
                                    formatValue={(v) => (v < 10 ? "0" + v : v) + ":00"}
                                />
                            </div>
                            <div className="col-12">
                                    <div className="form-checkbox d-flex items-center">
                                        <input type="checkbox" id="any-airport" checked={anyAirport}
                                               onChange={(e) => setAnyAirport(e.target.checked)}
                                               aria-label="Szukaj z dowolnego lotniska w Polsce"/>
                                        <div className="form-checkbox__mark">
                                            <div className="form-checkbox__icon icon-check"/>
                                        </div>
                                        <div className="text-15 ml-10">Szukaj ze wszystkich lotnisk</div>
                                    </div>
                                </div>
                        </div>
                    )}

                    {/*<FlyingToLocation onLocationChange={setToLocation} />*/}
                    {/* End Location Flying To */}

                    {/*<div className="searchMenu-date px-30 lg:py-20 lg:px-0 js-form-dd js-calendar">*/}
                    {/*    <div>*/}
                    {/*        <h4 className="text-15 fw-500 ls-2 lh-16">Zakres wyjazdu</h4>*/}
                    {/*        /!*<DateSearch onDateChange={setTripDate} />*!/*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                </div>
            </div>
                    {/* End .mainSearch */}
        </>
    );
};

export default MainFilterSearchBox;
